// Staff cards editor (Saturs → Komanda).
//
// These cards render on /kontakti and /komanda. Until now the only way to
// change them was a direct database write — so removing someone who had left,
// or filling in the "Atbild par" line, needed a developer. This is that
// surface.
//
// Rows live in staff_members, reached through the generic CRUD proxy (the
// table is already allow-listed). `bio` holds the "Atbild par" responsibility
// line the storefront renders under the job title.
//
// Deactivating is the normal way to remove someone: the card disappears from
// the public page but the row survives, so the change is reversible. Deleting
// is offered too, behind a confirmation, for rows added by mistake.

import { useEffect, useMemo, useState } from "react";
import { supabase } from "@vinoga/db";
import { ArrowDown, ArrowUp, Plus, Save, Search, Trash2, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ImageUpload } from "@/components/admin/ImageUpload";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { useT } from "@/lib/i18n";

type StaffRow = {
  id: string;
  name: string;
  role: string | null;
  bio: string | null;
  image_url: string | null;
  email: string | null;
  phone: string | null;
  position: number;
  active: boolean;
};

const blank = (position: number): StaffRow => ({
  id: "",
  name: "",
  role: "",
  bio: "",
  image_url: "",
  email: "",
  phone: "",
  position,
  active: true,
});

export function Staff() {
  const t = useT();
  const { toast } = useToast();
  const [rows, setRows] = useState<StaffRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<StaffRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("staff_members")
      .select("*")
      .order("position", { ascending: true });
    if (error) {
      toast({
        title: t("staff.errorLoading", "Neizdevās ielādēt komandu"),
        description: error.message,
        variant: "destructive",
      });
    } else {
      setRows((data ?? []) as StaffRow[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter(
      (r) =>
        r.name.toLowerCase().includes(s) ||
        (r.role ?? "").toLowerCase().includes(s) ||
        (r.bio ?? "").toLowerCase().includes(s),
    );
  }, [rows, search]);

  const save = async () => {
    if (!editing) return;
    const name = editing.name.trim();
    if (!name) {
      toast({
        title: t("staff.nameRequired", "Vārds ir obligāts"),
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    // Empty strings are stored as NULL so the storefront's `{p.role && …}`
    // checks hide the line entirely rather than rendering a blank row.
    const payload = {
      name,
      role: editing.role?.trim() || null,
      bio: editing.bio?.trim() || null,
      image_url: editing.image_url?.trim() || null,
      email: editing.email?.trim() || null,
      phone: editing.phone?.trim() || null,
      position: editing.position,
      active: editing.active,
    };

    const { error } = editing.id
      ? await supabase.from("staff_members").update(payload).eq("id", editing.id)
      : await supabase.from("staff_members").insert(payload);

    setSaving(false);
    if (error) {
      toast({
        title: t("common.saveFailed", "Saglabāšana neizdevās"),
        description: error.message,
        variant: "destructive",
      });
      return;
    }
    toast({ title: t("staff.saved", "Saglabāts") });
    setEditing(null);
    refresh();
  };

  /** Toggle visibility straight from the list — the common case. */
  const toggleActive = async (row: StaffRow) => {
    const next = !row.active;
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, active: next } : r)));
    const { error } = await supabase
      .from("staff_members")
      .update({ active: next })
      .eq("id", row.id);
    if (error) {
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, active: row.active } : r)));
      toast({
        title: t("common.saveFailed", "Saglabāšana neizdevās"),
        description: error.message,
        variant: "destructive",
      });
    }
  };

  /** Swap `position` with the neighbour so the public order is editable. */
  const move = async (row: StaffRow, dir: -1 | 1) => {
    const ordered = [...rows].sort((a, b) => a.position - b.position);
    const i = ordered.findIndex((r) => r.id === row.id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= ordered.length) return;

    const a = ordered[i];
    const b = ordered[j];
    if (!a || !b) return;
    // Positions can collide in the imported data; fall back to index order so
    // a swap still moves the row rather than silently doing nothing.
    const posA = a.position === b.position ? i : a.position;
    const posB = a.position === b.position ? j : b.position;

    setRows((prev) =>
      prev.map((r) =>
        r.id === a.id ? { ...r, position: posB } : r.id === b.id ? { ...r, position: posA } : r,
      ),
    );
    const [r1, r2] = await Promise.all([
      supabase.from("staff_members").update({ position: posB }).eq("id", a.id),
      supabase.from("staff_members").update({ position: posA }).eq("id", b.id),
    ]);
    if (r1.error || r2.error) {
      toast({
        title: t("common.saveFailed", "Saglabāšana neizdevās"),
        description: (r1.error ?? r2.error)?.message,
        variant: "destructive",
      });
      refresh();
    }
  };

  const remove = async () => {
    if (!deleteId) return;
    const id = deleteId;
    setDeleteId(null);
    const { error } = await supabase.from("staff_members").delete().eq("id", id);
    if (error) {
      toast({
        title: t("common.deleteFailed", "Dzēšana neizdevās"),
        description: error.message,
        variant: "destructive",
      });
      return;
    }
    toast({ title: t("staff.deleted", "Dzēsts") });
    refresh();
  };

  const nextPosition = rows.length ? Math.max(...rows.map((r) => r.position)) + 1 : 1;
  const visibleCount = rows.filter((r) => r.active).length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("staff.title", "Komanda")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t(
              "staff.subtitle",
              "Kartītes, kas redzamas lapā Kontakti. Neaktīvie netiek rādīti publiski. Izmaiņas mājaslapā parādās līdz 10 minūšu laikā.",
            )}
          </p>
        </div>
        <Button onClick={() => setEditing(blank(nextPosition))}>
          <Plus className="mr-2 size-4" />
          {t("staff.add", "Pievienot cilvēku")}
        </Button>
      </div>

      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder={t("staff.search", "Meklēt pēc vārda vai amata…")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Badge variant="secondary">
            {t("staff.visibleCount", "Publiski redzami")}: {visibleCount}
          </Badge>
        </div>
      </Card>

      {loading ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          {t("common.loading", "Ielādē…")}
        </Card>
      ) : filtered.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          {t("staff.empty", "Nav neviena ieraksta.")}
        </Card>
      ) : (
        <div className="grid gap-3">
          {filtered.map((row, idx) => (
            <Card
              key={row.id}
              className={`flex flex-wrap items-center gap-4 p-4 ${row.active ? "" : "opacity-60"}`}
            >
              <div className="size-14 shrink-0 overflow-hidden rounded-full bg-muted">
                {row.image_url ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={row.image_url}
                    alt={row.name}
                    className="size-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="grid size-full place-items-center text-sm font-semibold text-muted-foreground">
                    {row.name
                      .split(/\s+/)
                      .filter(Boolean)
                      .slice(0, 2)
                      .map((w) => w[0]?.toUpperCase() ?? "")
                      .join("")}
                  </div>
                )}
              </div>

              <div className="min-w-[200px] flex-1">
                <div className="font-medium">{row.name}</div>
                {row.role && <div className="text-sm text-muted-foreground">{row.role}</div>}
                {row.bio && (
                  <div className="mt-1 text-xs text-muted-foreground line-clamp-2">{row.bio}</div>
                )}
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={idx === 0}
                  onClick={() => move(row, -1)}
                  title={t("staff.moveUp", "Pārvietot augšup")}
                >
                  <ArrowUp className="size-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={idx === filtered.length - 1}
                  onClick={() => move(row, 1)}
                  title={t("staff.moveDown", "Pārvietot lejup")}
                >
                  <ArrowDown className="size-4" />
                </Button>
              </div>

              <div className="flex items-center gap-2">
                <Switch checked={row.active} onCheckedChange={() => toggleActive(row)} />
                <span className="w-20 text-xs text-muted-foreground">
                  {row.active ? t("staff.visible", "Redzams") : t("staff.hidden", "Paslēpts")}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setEditing(row)}>
                  {t("common.edit", "Rediģēt")}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setDeleteId(row.id)}
                  title={t("common.delete", "Dzēst")}
                >
                  <Trash2 className="size-4 text-destructive" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">
          <Card className="max-h-[90vh] w-full max-w-2xl overflow-y-auto p-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                {editing.id
                  ? t("staff.editTitle", "Rediģēt kartīti")
                  : t("staff.addTitle", "Jauna kartīte")}
              </h2>
              <Button variant="ghost" size="icon" onClick={() => setEditing(null)}>
                <X className="size-4" />
              </Button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium">
                  {t("staff.fieldName", "Vārds, uzvārds")} *
                </label>
                <Input
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  placeholder="Edgars Miglāns"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium">
                  {t("staff.fieldRole", "Ikdienas amats")}
                </label>
                <Input
                  value={editing.role ?? ""}
                  onChange={(e) => setEditing({ ...editing, role: e.target.value })}
                  placeholder={t("staff.rolePlaceholder", "Vairumtirdzniecības vadītājs")}
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium">
                  {t("staff.fieldBio", "Atbild par")}
                </label>
                <Textarea
                  rows={3}
                  value={editing.bio ?? ""}
                  onChange={(e) => setEditing({ ...editing, bio: e.target.value })}
                  placeholder={t(
                    "staff.bioPlaceholder",
                    "HoReCa un B2B sadarbība, vairumtirdzniecības komanda",
                  )}
                />
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {t(
                    "staff.bioHint",
                    "Īss apraksts, kas parādās zem amata nosaukuma publiskajā kartītē.",
                  )}
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-medium">
                    {t("staff.fieldEmail", "E-pasts")}
                  </label>
                  <Input
                    type="email"
                    value={editing.email ?? ""}
                    onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                    placeholder="vards@vinoga.lv"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium">
                    {t("staff.fieldPhone", "Tālrunis")}
                  </label>
                  <Input
                    value={editing.phone ?? ""}
                    onChange={(e) => setEditing({ ...editing, phone: e.target.value })}
                    placeholder="26444488"
                  />
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium">
                  {t("staff.fieldPhoto", "Foto")}
                </label>
                <ImageUpload
                  value={editing.image_url ?? ""}
                  onChange={(url) => setEditing({ ...editing, image_url: url })}
                  bucket="products"
                  placeholder={t("staff.uploadHint", "Ievelc foto šeit (vai klikšķini, lai izvēlētos)")}
                />
                <p className="mt-2 text-[11px] text-muted-foreground">
                  {t(
                    "staff.photoHint",
                    "Kartītē foto tiek rādīts kvadrātā. Ieteicams vismaz 600 × 600 px.",
                  )}
                </p>
                {editing.image_url && (
                  <details className="mt-3">
                    <summary className="cursor-pointer text-[11px] text-muted-foreground">
                      {t("staff.showUrl", "Rādīt / ielīmēt URL")}
                    </summary>
                    <Input
                      className="mt-2 font-mono text-xs"
                      value={editing.image_url}
                      onChange={(e) => setEditing({ ...editing, image_url: e.target.value })}
                    />
                  </details>
                )}
              </div>

              <div className="flex items-center gap-3 rounded-md border p-3">
                <Switch
                  checked={editing.active}
                  onCheckedChange={(v) => setEditing({ ...editing, active: v })}
                />
                <div>
                  <div className="text-sm font-medium">
                    {t("staff.fieldActive", "Rādīt publiski")}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {t(
                      "staff.activeHint",
                      "Izslēdz, ja cilvēks vairs nestrādā — kartīte pazūd no mājaslapas, bet ieraksts saglabājas.",
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditing(null)}>
                {t("common.cancel", "Atcelt")}
              </Button>
              <Button onClick={save} disabled={saving}>
                <Save className="mr-2 size-4" />
                {saving ? t("common.saving", "Saglabā…") : t("common.save", "Saglabāt")}
              </Button>
            </div>
          </Card>
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("staff.deleteTitle", "Dzēst kartīti?")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "staff.deleteDescription",
                "Ieraksts tiks dzēsts neatgriezeniski. Ja cilvēks vienkārši vairs nestrādā, labāk izslēdz “Rādīt publiski”.",
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel", "Atcelt")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={remove}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("common.delete", "Dzēst")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
